<template>
    <div class="news-page">
        <!-- Hero Section -->
        <div class="news-hero">
            <div class="news-hero_background">
                <div class="news-hero_particles"></div>
                <div class="news-hero_gradient"></div>
            </div>

            <div class="news-hero_content">
                <div class="news-hero_badge">
                    <sw-icon name="star-full" />
                    <span>Novidades FileSave</span>
                </div>

                <h1 class="news-hero_title">
                    O futuro da gestão de documentos está aqui
                </h1>

                <p class="news-hero_subtitle">
                    Descubra as inovações que estão transformando a forma como você organiza,
                    protege e compartilha seus documentos mais importantes.
                </p>
            </div>
        </div>

        <!-- Navigation Pills -->
        <div class="news-navigation">
            <div class="news-navigation_container">
                <div class="news-pills">
                    <button
                        v-for="tab in navigationTabs"
                        :key="tab.key"
                        class="pill"
                        :class="{ 'pill--active': activeTab === tab.key }"
                        @click="setActiveTab(tab.key)"
                    >
                        <sw-icon :name="tab.icon" />
                        <span>{{ tab.label }}</span>
                        <div class="pill-badge" v-if="tab.count">{{ tab.count }}</div>
                    </button>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="news-content">

            <!-- Latest Updates Section -->
            <section v-if="activeTab === 'latest'" class="news-section">
                <div class="section-header">
                    <h2>Últimas Atualizações</h2>
                    <p>Confira as novidades mais recentes da plataforma</p>
                </div>

                <div class="updates-grid">
                    <article
                        v-for="update in latestUpdates"
                        :key="update.id"
                        class="update-card"
                        :class="`update-card--${update.type}`"
                    >
                        <div class="update-card_header">
                            <div class="update-badge">
                                <sw-icon :name="getUpdateIcon(update.type)" />
                                <span>{{ getUpdateLabel(update.type) }}</span>
                            </div>
                            <div class="update-date">{{ formatDate(update.date) }}</div>
                        </div>

                        <div class="update-card_content">
                            <h3>{{ update.title }}</h3>
                            <p>{{ update.description }}</p>

                            <div class="update-features" v-if="update.features">
                                <h4>Principais melhorias:</h4>
                                <ul>
                                    <li v-for="feature in update.features" :key="feature">
                                        <sw-icon name="checkmark" />
                                        <span>{{ feature }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div class="update-card_footer">
                            <sw-button
                                name="Saber Mais"
                                icon="arrow-right"
                                variant="outline"
                                size="small"
                                @click="openUpdateDetails(update.id)"
                            />
                        </div>
                    </article>
                </div>
            </section>

            <!-- Roadmap Section -->
            <section v-if="activeTab === 'roadmap'" class="news-section">
                <div class="section-header">
                    <h2>Roadmap de Desenvolvimento</h2>
                    <p>Veja o que está sendo desenvolvido para você</p>
                </div>

                <!-- Status Flow -->
                <div class="status-flow">
                    <h3>Da ideia até suas mãos: nossa jornada de desenvolvimento</h3>

                    <div class="flow-container">
                        <!-- Progress Line Background -->
                        <div class="flow-line"></div>

                        <!-- Status Steps -->
                        <div class="flow-steps">
                            <div
                                v-for="(statusInfo, statusKey, index) in statusDescriptions"
                                :key="statusKey"
                                class="flow-step"
                                :class="{ 'flow-step--active': index <= 2 }"
                            >
                                <!-- Step Circle -->
                                <div class="step-circle" :style="{
                                    backgroundColor: statusInfo.color,
                                    boxShadow: `0 4px 20px ${statusInfo.color}40`
                                }">
                                    <div class="step-emoji">{{ statusInfo.emoji }}</div>
                                    <div class="step-number">{{ index + 1 }}</div>
                                </div>

                                <!-- Step Content -->
                                <div class="step-content">
                                    <div class="step-badge" :style="{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color }">
                                        {{ statusInfo.title }}
                                    </div>
                                    <h4>{{ getStepTitle(statusKey) }}</h4>
                                    <p>{{ statusInfo.description }}</p>

                                    <!-- Timeline Period Badge -->
                                    <div class="step-timeline-badge" v-if="getTimelinePeriod(statusKey)">
                                        <sw-icon name="calendar" />
                                        <span>{{ getTimelinePeriod(statusKey) }}</span>
                                    </div>
                                </div>

                                <!-- Arrow Connector -->
                                <div v-if="index < Object.keys(statusDescriptions).length - 1" class="flow-arrow">
                                    <sw-icon name="arrow-right" />
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div class="roadmap-timeline">
                    <div
                        v-for="(quarter, index) in roadmapQuarters"
                        :key="quarter.id"
                        class="timeline-quarter"
                    >
                        <div class="quarter-header">
                            <div class="quarter-badge">{{ quarter.period }}</div>
                            <h3>{{ quarter.theme }}</h3>
                            <p>{{ quarter.description }}</p>
                        </div>

                        <div class="quarter-features">
                            <div
                                v-for="feature in quarter.features"
                                :key="feature.id"
                                class="timeline-feature"
                                :class="`timeline-feature--${feature.status}`"
                            >
                                <div class="feature-status">
                                    <sw-icon :name="getStatusIcon(feature.status)" />
                                </div>

                                <div class="feature-content">
                                    <h4>{{ feature.title }}</h4>
                                    <p>{{ feature.description }}</p>

                                    <div class="feature-meta">
                                        <span class="feature-category">{{ feature.category }}</span>
                                        <div class="feature-votes">
                                            <sw-icon name="heart" />
                                            <span>{{ feature.votes }} votos</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            <!-- Changelog Section -->
            <section v-if="activeTab === 'changelog'" class="news-section">
                <div class="section-header">
                    <h2>Histórico de Versões</h2>
                    <p>Acompanhe todas as melhorias implementadas</p>
                </div>

                <div class="changelog-timeline">
                    <div
                        v-for="version in changelog"
                        :key="version.id"
                        class="changelog-item"
                    >
                        <div class="version-badge">
                            <span>v{{ version.version }}</span>
                        </div>

                        <div class="version-content">
                            <div class="version-header">
                                <h3>{{ version.title }}</h3>
                                <div class="version-date">{{ formatDate(version.date) }}</div>
                            </div>

                            <div class="version-changes">
                                <div v-if="version.added.length" class="change-group">
                                    <h4><sw-icon name="plus" /> Novos Recursos</h4>
                                    <ul>
                                        <li v-for="item in version.added" :key="item">{{ item }}</li>
                                    </ul>
                                </div>

                                <div v-if="version.improved.length" class="change-group">
                                    <h4><sw-icon name="trending-up" /> Melhorias</h4>
                                    <ul>
                                        <li v-for="item in version.improved" :key="item">{{ item }}</li>
                                    </ul>
                                </div>

                                <div v-if="version.fixed.length" class="change-group">
                                    <h4><sw-icon name="checkmark" /> Correções</h4>
                                    <ul>
                                        <li v-for="item in version.fixed" :key="item">{{ item }}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>

    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';

    import { mockNewsUpdates, mockRoadmapQuarters, mockChangelog } from '@/data/mockData';

    const activeTab = ref('latest');

    const latestUpdates = ref([]);
    const roadmapQuarters = ref([]);
    const changelog = ref([]);

    const navigationTabs = [
        { key: 'latest', label: 'Últimas', icon: 'clock', count: null },
        { key: 'roadmap', label: 'Roadmap', icon: 'map', count: 8 },
        { key: 'changelog', label: 'Versões', icon: 'list', count: null }
    ];


    const setActiveTab = (tab) => {
        activeTab.value = tab;
    };

    const getUpdateIcon = (type) => {
        const icons = {
            feature: 'star',
            improvement: 'trending-up',
            security: 'shield',
            bug: 'checkmark'
        };
        return icons[type] || 'circle';
    };

    const getUpdateLabel = (type) => {
        const labels = {
            feature: 'Novo Recurso',
            improvement: 'Melhoria',
            security: 'Segurança',
            bug: 'Correção'
        };
        return labels[type] || type;
    };

    const getStatusIcon = (status) => {
        const icons = {
            planned: 'calendar',
            analysis: 'search-glass',
            development: 'code',
            testing: 'checkmark-circle',
            available: 'star-full'
        };
        return icons[status] || 'circle';
    };

    const statusDescriptions = {
        planned: {
            title: 'Planejado',
            description: 'Esta funcionalidade está no nosso radar! Nossa equipe já definiu como vai funcionar e está organizando o cronograma para começar o desenvolvimento.',
            color: '#6b7280',
            emoji: '📋'
        },
        analysis: {
            title: 'Em Análise',
            description: 'Estamos estudando a melhor forma de implementar este recurso. Nossa equipe está pesquisando, criando protótipos e validando a experiência do usuário.',
            color: '#f59e0b',
            emoji: '🔍'
        },
        development: {
            title: 'Em Desenvolvimento',
            description: 'Mãos à obra! Nossos desenvolvedores estão programando esta funcionalidade com muito carinho. Em breve você poderá usar!',
            color: '#3b82f6',
            emoji: '⚡'
        },
        testing: {
            title: 'Em Testes',
            description: 'Quase pronto! Estamos testando tudo para garantir que funcione perfeitamente antes de liberar para você. Segurança e qualidade em primeiro lugar!',
            color: '#8b5cf6',
            emoji: '🧪'
        },
        available: {
            title: 'Disponível',
            description: 'Pronto para usar! Esta funcionalidade já está disponível na sua conta. Aproveite e nos conte o que achou!',
            color: '#10b981',
            emoji: '🎉'
        }
    };

    const getStatusDescription = (status) => {
        return statusDescriptions[status] || {
            title: status,
            description: 'Status da funcionalidade',
            color: '#6b7280',
            emoji: '⭕'
        };
    };

    const getStepTitle = (statusKey) => {
        const titles = {
            planned: 'Idealizando a solução',
            analysis: 'Pesquisando e prototipando',
            development: 'Programando com carinho',
            testing: 'Testando para perfeição',
            available: 'Pronto para você usar!'
        };
        return titles[statusKey] || 'Etapa do desenvolvimento';
    };

    const getTimelinePeriod = (statusKey) => {
        // Mapear status para períodos do roadmap baseado na análise dos dados
        const statusToPeriod = {
            planned: 'Futuro Próximo',
            analysis: 'Próximos Meses',
            development: 'Próximas Semanas',
            testing: 'Próximas Semanas',
            available: null // Já está disponível, não tem período futuro
        };
        return statusToPeriod[statusKey];
    };



    const formatDate = (date) => {
        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        }).format(new Date(date));
    };

    const openUpdateDetails = (updateId) => {
        console.log('Abrir detalhes do update:', updateId);
    };




    const loadNewsData = () => {
        latestUpdates.value = mockNewsUpdates;
        roadmapQuarters.value = mockRoadmapQuarters;
        changelog.value = mockChangelog;
    };

    onMounted(() => {
        loadNewsData();
    });
</script>

<style lang="scss" scoped>
@import '@/assets/css/mediaScreen.scss';

.news-page {
    min-height: 100vh;
    background: var(--backgroundDashboard);
}

/* Hero Section */
.news-hero {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: white;
    padding: 30px 0px;

    &_background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    &_gradient {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.9), rgba(139, 92, 246, 0.8));
    }

    &_particles {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image:
            radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.1) 2px, transparent 2px),
            radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            radial-gradient(circle at 40% 60%, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
        background-size: 100px 100px, 150px 150px, 80px 80px;
        animation: float 20s ease-in-out infinite;
    }

    &_content {
        position: relative;
        z-index: 2;
        text-align: center;
        max-width: 800px;
        padding: 2em;
    }

    &_badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5em;
        padding: 0.5em 1.5em;
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        border-radius: 50px;
        border: 1px solid rgba(255, 255, 255, 0.3);
        margin-bottom: 2em;
        font-size: 0.9em;
        font-weight: 600;

        .sw-icon {
            font-size: 1.2em;
        }
    }

    &_title {
        font-size: 3.5em;
        font-weight: 800;
        margin-bottom: 0.5em;
        line-height: 1.1;
        background: linear-gradient(135deg, #ffffff, #e2e8f0);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;

        @include screen_medias(s, m) {
            font-size: 2.5em;
        }
    }

    &_subtitle {
        font-size: 1.25em;
        margin-bottom: 3em;
        opacity: 0.9;
        line-height: 1.6;

        @include screen_medias(s, m) {
            font-size: 1.1em;
            margin-bottom: 2em;
        }
    }

}

@keyframes float {
    0%, 100% { transform: translateY(0px) translateX(0px); }
    25% { transform: translateY(-10px) translateX(5px); }
    50% { transform: translateY(-5px) translateX(-5px); }
    75% { transform: translateY(-15px) translateX(3px); }
}

/* Navigation */
.news-navigation {
    background: white;
    border-bottom: 1px solid var(--borderColorLight);

    &_container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2em;
    }
}

.news-pills {
    display: flex;
    gap: 1em;
    padding: 1.5em 0;
    overflow-x: auto;

    @include screen_medias(s, m) {
        gap: 0.5em;
        padding: 1em 0;
    }
}

.pill {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.75em 1.5em;
    border: none;
    background: var(--backgroundHover);
    color: var(--textSecondary);
    border-radius: 50px;
    font-weight: 600;
    font-size: 0.9em;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    position: relative;

    &:hover {
        background: var(--linkColor);
        color: white;
        transform: translateY(-2px);
    }

    &--active {
        background: var(--linkColor);
        color: white;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }

    .pill-badge {
        background: rgba(255, 255, 255, 0.2);
        color: white;
        padding: 0.2em 0.5em;
        border-radius: 12px;
        font-size: 0.8em;
        font-weight: 700;
        min-width: 20px;
        text-align: center;
    }
}

/* Main Content */
.news-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 3em 2em;

    @include screen_medias(s, m) {
        padding: 2em 1em;
    }
}

.news-section {
    margin-bottom: 4em;
}

.section-header {
    text-align: center;
    margin-bottom: 3em;

    h2 {
        font-size: 2.5em;
        font-weight: 800;
        color: var(--textPrimary);
        margin-bottom: 0.5em;
    }

    p {
        font-size: 1.1em;
        color: var(--textSecondary);
    }
}

/* Updates Grid */
.updates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 2em;

    @include screen_medias(s, m) {
        grid-template-columns: 1fr;
        gap: 1.5em;
    }
}

.update-card {
    background: white;
    border-radius: 16px;
    padding: 2em;
    border: 1px solid var(--borderColorLight);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: var(--linkColor);
    }

    &--feature::before { background: #10b981; }
    &--improvement::before { background: #f59e0b; }
    &--security::before { background: #ef4444; }
    &--bug::before { background: #8b5cf6; }

    &_header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5em;
    }

    &_content {
        h3 {
            font-size: 1.25em;
            font-weight: 700;
            color: var(--textPrimary);
            margin-bottom: 0.75em;
        }

        p {
            color: var(--textSecondary);
            line-height: 1.6;
            margin-bottom: 1.5em;
        }
    }

    &_footer {
        margin-top: 2em;
    }
}

.update-badge {
    display: flex;
    align-items: center;
    gap: 0.5em;
    padding: 0.5em 1em;
    background: var(--backgroundHover);
    border-radius: 20px;
    font-size: 0.8em;
    font-weight: 600;
    color: var(--textSecondary);

    .sw-icon {
        font-size: 1em;
    }
}

.update-date {
    font-size: 0.85em;
    color: var(--textMuted);
}

.update-features {
    h4 {
        font-size: 0.9em;
        font-weight: 600;
        color: var(--textPrimary);
        margin-bottom: 0.75em;
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
            display: flex;
            align-items: center;
            gap: 0.5em;
            padding: 0.25em 0;
            font-size: 0.9em;
            color: var(--textSecondary);

            .sw-icon {
                color: var(--colorGreen);
                font-size: 0.8em;
            }
        }
    }
}

/* Status Flow */
.status-flow {
    margin-bottom: 4em;
    text-align: center;

    h3 {
        font-size: 2em;
        font-weight: 800;
        color: var(--textPrimary);
        margin-bottom: 3em;
        background: linear-gradient(135deg, #3b82f6, #8b5cf6);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;

        @include screen_medias(s, m) {
            font-size: 1.5em;
            margin-bottom: 2em;
        }
    }
}

.flow-container {
    position: relative;
    margin-bottom: 4em;
    padding: 2em 0;
}

.flow-line {
    position: absolute;
    top: 50%;
    left: 10%;
    right: 10%;
    height: 4px;
    background: linear-gradient(90deg, #e5e7eb, #3b82f6, #8b5cf6, #10b981);
    border-radius: 2px;
    transform: translateY(-50%);
    z-index: 1;

    @include screen_medias(s, m) {
        display: none;
    }
}

.flow-steps {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    z-index: 2;
    max-width: 1400px;
    margin: 0 auto;

    @include screen_medias(s, m) {
        flex-direction: column;
        gap: 2em;
        align-items: center;
    }
}

.flow-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
    max-width: 250px;
    position: relative;

    &--active {
        .step-circle {
            animation: pulse-glow 2s ease-in-out infinite;
        }
    }

    @include screen_medias(s, m) {
        max-width: 100%;
        width: 100%;
    }
}

.step-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    margin-bottom: 1.5em;
    position: relative;
    z-index: 3;
    border: 4px solid white;

    .step-emoji {
        font-size: 1.5em;
        line-height: 1;
        margin-bottom: 0.25em;
    }

    .step-number {
        font-size: 0.7em;
        opacity: 0.8;
        font-weight: 800;
    }
}

.step-content {
    background: white;
    border-radius: 16px;
    padding: 1.5em;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--borderColorLight);
    transition: all 0.3s ease;
    width: 100%;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    }

    .step-badge {
        display: inline-block;
        padding: 0.5em 1em;
        border-radius: 20px;
        font-size: 0.8em;
        font-weight: 700;
        margin-bottom: 1em;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    h4 {
        font-size: 1.1em;
        font-weight: 700;
        color: var(--textPrimary);
        margin-bottom: 0.75em;
        line-height: 1.3;
    }

    p {
        color: var(--textSecondary);
        line-height: 1.6;
        font-size: 0.9em;
        margin: 0 0 1em 0;
    }

    .step-timeline-badge {
        display: flex;
        align-items: center;
        gap: 0.5em;
        padding: 0.5em 1em;
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
        border: 1px solid rgba(59, 130, 246, 0.2);
        border-radius: 20px;
        font-size: 0.8em;
        font-weight: 600;
        color: var(--linkColor);
        margin-top: 0.5em;

        .sw-icon {
            font-size: 1em;
            opacity: 0.8;
        }
    }
}

.flow-arrow {
    position: absolute;
    right: -25px;
    top: 40px;
    color: var(--linkColor);
    font-size: 1.5em;
    z-index: 4;
    background: white;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    animation: bounce-right 2s ease-in-out infinite;

    @include screen_medias(s, m) {
        display: none;
    }
}


@keyframes pulse-glow {
    0%, 100% {
        box-shadow: 0 4px 20px rgba(59, 130, 246, 0.4);
        transform: scale(1);
    }
    50% {
        box-shadow: 0 8px 30px rgba(59, 130, 246, 0.6);
        transform: scale(1.05);
    }
}

@keyframes bounce-right {
    0%, 100% {
        transform: translateX(0);
    }
    50% {
        transform: translateX(5px);
    }
}

/* Roadmap Timeline */
.roadmap-timeline {
    display: flex;
    flex-direction: column;
    gap: 3em;
}

.timeline-quarter {
    position: relative;
    padding-left: 2em;

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 2px;
        background: linear-gradient(to bottom, var(--linkColor), transparent);
    }

    .quarter-header {
        margin-bottom: 2em;

        .quarter-badge {
            display: inline-block;
            padding: 0.5em 1em;
            background: var(--linkColor);
            color: white;
            border-radius: 20px;
            font-size: 0.8em;
            font-weight: 700;
            margin-bottom: 1em;
        }

        h3 {
            font-size: 1.5em;
            font-weight: 700;
            color: var(--textPrimary);
            margin-bottom: 0.5em;
        }

        p {
            color: var(--textSecondary);
            font-size: 1.1em;
        }
    }
}

.quarter-features {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.timeline-feature {
    display: flex;
    gap: 1em;
    padding: 1.5em;
    background: white;
    border-radius: 12px;
    border: 1px solid var(--borderColorLight);
    transition: all 0.3s ease;

    &:hover {
        transform: translateX(8px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    }

    .feature-status {
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--backgroundHover);
        color: var(--textMuted);

        .sw-icon {
            font-size: 1.2em;
        }
    }

    &--planned .feature-status {
        background: rgba(156, 163, 175, 0.2);
        color: var(--textSecondary);
    }

    &--analysis .feature-status {
        background: rgba(251, 191, 36, 0.2);
        color: #d97706;
    }

    &--development .feature-status {
        background: rgba(59, 130, 246, 0.2);
        color: var(--linkColor);
    }

    &--testing .feature-status {
        background: rgba(139, 92, 246, 0.2);
        color: #8b5cf6;
    }

    &--available .feature-status {
        background: rgba(34, 197, 94, 0.2);
        color: var(--colorGreen);
    }

    .feature-content {
        flex: 1;

        h4 {
            font-size: 1.1em;
            font-weight: 700;
            color: var(--textPrimary);
            margin-bottom: 0.5em;
        }

        p {
            color: var(--textSecondary);
            line-height: 1.5;
            margin-bottom: 1em;
        }
    }

    .feature-meta {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .feature-category {
            padding: 0.25em 0.75em;
            background: var(--backgroundHover);
            border-radius: 12px;
            font-size: 0.75em;
            font-weight: 600;
            color: var(--textSecondary);
        }

        .feature-votes {
            display: flex;
            align-items: center;
            gap: 0.25em;
            font-size: 0.85em;
            color: var(--textMuted);

            .sw-icon {
                color: var(--colorRed);
            }
        }
    }
}


/* Changelog */
.changelog-timeline {
    display: flex;
    flex-direction: column;
    gap: 2em;
}

.changelog-item {
    display: flex;
    gap: 1.5em;
    padding: 2em;
    background: white;
    border-radius: 16px;
    border: 1px solid var(--borderColorLight);

    .version-badge {
        flex-shrink: 0;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: var(--linkColor);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.9em;
    }

    .version-content {
        flex: 1;
    }

    .version-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5em;

        h3 {
            font-size: 1.25em;
            font-weight: 700;
            color: var(--textPrimary);
        }

        .version-date {
            font-size: 0.9em;
            color: var(--textMuted);
        }
    }
}

.version-changes {
    display: flex;
    flex-direction: column;
    gap: 1.5em;
}

.change-group {
    h4 {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 1em;
        font-weight: 600;
        color: var(--textPrimary);
        margin-bottom: 0.75em;

        .sw-icon {
            font-size: 1em;
            color: var(--linkColor);
        }
    }

    ul {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
            padding: 0.25em 0;
            color: var(--textSecondary);
            font-size: 0.95em;
            line-height: 1.5;

            &::before {
                content: '•';
                color: var(--linkColor);
                margin-right: 0.5em;
                font-weight: bold;
            }
        }
    }
}

</style>